// Pure form state / validation / payload logic for the Materials page
// "Generate with AI" modal. Kept free of React so it can be unit-tested
// directly (see generateDeckForm.test.ts).
import type { DeckContentFocus, DeckColorTheme, DeckOutputFormat, EmailTone } from '@/types/workflow'

export interface GenerateDeckFormState {
  title: string
  materialIds: number[]
  slideCount: number
  contentFocus: DeckContentFocus
  tone: EmailTone
  customInstructions: string
  colorTheme: DeckColorTheme
  customColors?: { primary: string; accent: string; secondary: string }
  logoUrl: string
  outputFormat: DeckOutputFormat
  // Static audience targeting, picked from the pre-defined dropdowns in
  // lib/deckOptions (deckTargetRoleOptions / deckTargetIndustryOptions) and
  // baked into the deck's content at generation time. '' = untargeted.
  targetRole: string
  targetIndustry: string
  // The deck's source is EITHER selected materials OR a website to scrape —
  // a tabbed choice in the modal. Both values persist in state (switching
  // tabs doesn't wipe input), but only the active tab's source is submitted.
  sourceType: DeckSourceType
  websiteUrl: string
}

export type DeckSourceType = 'materials' | 'website'

export const defaultGenerateDeckForm: GenerateDeckFormState = {
  title: '',
  materialIds: [],
  slideCount: 6,
  contentFocus: 'pitch_deck',
  tone: 'professional',
  customInstructions: '',
  colorTheme: 'auto_from_brand',
  customColors: undefined,
  logoUrl: '',
  outputFormat: 'pdf',
  targetRole: '',
  targetIndustry: '',
  sourceType: 'materials',
  websiteUrl: '',
}

/** True when the string parses as an http(s) URL. */
export function isValidHttpUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

// Per-recipient personalization was removed entirely (2026-08-25): decks
// are plain shared files — no merge fields, no per-contact work at send
// time, so attaching an AI deck behaves exactly like attaching an upload.
// Decks generated BEFORE this change still carry tokens in their files;
// the send-path merge machinery stays alive for those legacy decks only.

/** Returns a user-facing error message, or null when the form is valid. */
export function validateGenerateDeckForm(form: GenerateDeckFormState): string | null {
  if (!form.title.trim()) return 'Give the deck a title.'
  // Validate whichever source tab is active — the other tab's state is
  // dormant and must never block submission.
  if (form.sourceType === 'materials') {
    if (form.materialIds.length === 0) {
      return 'Select at least one material source for the deck.'
    }
  } else {
    if (!form.websiteUrl.trim()) {
      return 'Enter the website URL to scrape for the deck.'
    }
    if (!isValidHttpUrl(form.websiteUrl.trim())) {
      return 'Enter a valid website URL (starting with http:// or https://).'
    }
  }
  if (form.slideCount < 3 || form.slideCount > 15) {
    return 'Number of slides must be between 3 and 15.'
  }
  if (form.outputFormat !== 'pdf' && form.outputFormat !== 'pptx') {
    return 'Output format must be PDF or PPTX.'
  }
  return null
}

/** Request body for POST /materials/generate-deck. Key names mirror the
 * backend's GenerateDeckRequest schema. */
export function buildGenerateDeckPayload(form: GenerateDeckFormState): Record<string, unknown> {
  return {
    title: form.title.trim(),
    // Only the ACTIVE source tab is submitted — the dormant tab's state
    // stays client-side so switching back doesn't lose input.
    material_ids: form.sourceType === 'materials' ? form.materialIds : [],
    slide_count: form.slideCount,
    content_focus: form.contentFocus,
    tone: form.tone,
    custom_instructions: form.customInstructions,
    color_theme: form.colorTheme,
    custom_colors: form.colorTheme === 'custom' ? form.customColors : undefined,
    logo_url: form.logoUrl || undefined,
    output_format: form.outputFormat,
    target_role: form.targetRole,
    target_industry: form.targetIndustry,
    website_url: form.sourceType === 'website' ? form.websiteUrl.trim() : '',
    personalization_tokens: [],
  }
}
